<template>
  <div v-loading="!isDataReady">
    <!-- 工具栏 -->
    <div class="toolbar">
      <SelectUser v-model:selectedMember="selectedMember" ref="selectUser" :element="element"></SelectUser>
      <el-radio-group v-model="dateType" style="margin-left: 16px">
        <el-radio label="day" :value="1">{{ $t('home.filter.day') }}</el-radio>
        <el-radio label="week" :value="2">{{ $t('home.filter.week') }}</el-radio>
        <el-radio label="month" :value="3">{{ $t('home.filter.month') }}</el-radio>
        <el-radio label="year" :value="4">{{ $t('home.filter.year') }}</el-radio>
      </el-radio-group>
    </div>
    <!-- 图表区域 -->
    <div class="chart-scroll-wrapper">
      <div ref="chartRef" class="chart-inner" :style="{ width: chartWidth + 'px', height }"></div>
    </div>
  </div>
</template>

<script setup>
import {init as initEcharts} from '@/utils/echarts/dashboardCore';
import {ref, computed, watch, onMounted, onBeforeUnmount, nextTick} from 'vue';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import SelectUser from "@/views/Home/components/SelectUser.vue";
import {useStore} from "vuex";
import getOverallMax, {convertDateList, convertMonthList, convertWeekDateList, niceInterval} from "@/views/Home/utils";
import {i18n} from "@/lang";

dayjs.extend(isoWeek); // 使用 ISO 周，周一为每周起始

const selectUser = ref()
const selectedMember = ref(null)
const store = useStore()
const props = defineProps({
  members: Array,
  width: {type: String, default: '100%'},
  height: {type: String, default: '400px'},
  element: {
    type: Object, default: () => ({})
  }
});

const chartRef = ref(null);
let chartInstance = null;
const dateType = ref(1);
const isDataReady = ref(false)
const lang = computed(() => store.state.system.langLocal)
const statisticsTeamEmailReplyVo = computed(() => store.state.homeEcharts.statisticsTeamEmailReplyVo)
const xData = computed(() => {
  const isEnglish = lang.value !== 'zh-CN'
  const targetData = statisticsTeamEmailReplyVo.value?.dateList ?? [];
  if (dateType.value === 1) {
    // 是英文
    if (isEnglish) {
      return convertDateList(targetData)
    } else {
      return targetData
    }
  } else if (dateType.value === 2) {
    if (isEnglish) {
      return convertWeekDateList(targetData)
    } else {
      return targetData
    }
  } else if (dateType.value === 3) {
    if (isEnglish) {
      return convertMonthList(targetData)
    } else {
      return targetData
    }
  } else {
    return targetData
  }
})


const seriesData = computed(() => {
  const data1 = statisticsTeamEmailReplyVo.value?.sendUnReplyList ?? []
  const data2 = statisticsTeamEmailReplyVo.value?.sendAlreadyReplyList ?? []
  const data3 = statisticsTeamEmailReplyVo.value?.receiveUnReplyList ?? []
  const data4 = statisticsTeamEmailReplyVo.value?.receiveAlreadyReplyList ?? []
  const emphasisStyle = {
    itemStyle: {
      shadowBlur: 10,
      shadowColor: 'rgba(0,0,0,0.3)'
    }
  };

  return [
    {
      name: i18n.global.t('home.email.e.chart.replay.1'),
      type: 'bar',
      stack: 'one',
      emphasis: emphasisStyle,
      data: data1
    },
    {
      name: i18n.global.t('home.email.e.chart.replay.2'),
      type: 'bar',
      stack: 'one',
      emphasis: emphasisStyle,
      data: data2
    },
    {
      name: i18n.global.t('home.email.e.chart.replay.3'),
      type: 'bar',
      stack: 'two',
      emphasis: emphasisStyle,
      data: data3
    },
    {
      name: i18n.global.t('home.email.e.chart.replay.4'),
      type: 'bar',
      stack: 'two',
      emphasis: emphasisStyle,
      data: data4
    }
  ];
});


// 计算最大值，避免为0导致坐标轴异常
const MaxNumber = computed(() => {
  const data1 = statisticsTeamEmailReplyVo.value?.sendUnReplyList ?? []
  const data2 = statisticsTeamEmailReplyVo.value?.sendAlreadyReplyList ?? []
  const data3 = statisticsTeamEmailReplyVo.value?.receiveUnReplyList ?? []
  const data4 = statisticsTeamEmailReplyVo.value?.receiveAlreadyReplyList ?? []
  const maxVal = getOverallMax([...data1], [...data2], [...data3], [...data4]);
  const safeMax = Math.max(0, Math.floor(maxVal));     // 向下取整，防止浮点数

  return safeMax > 0 ? safeMax : 10; // 保底为 10
})

// 计算合理间隔
// 获取合理间隔（niceInterval 要返回整数）
const intervalCom = computed(() => {
  return Math.max(1, Math.floor(niceInterval(MaxNumber.value, 5)));
});

function getYAxisConfig(granularity) {
  const max = MaxNumber.value;
  const interval = intervalCom.value;

  const ceilMax = Math.ceil(max / interval) * interval;

  return {
    min: 0,
    max: ceilMax,
    interval,
  };
}

const chartOption = computed(() => {
  if (!xData.value.length || !seriesData.value[0].data.length) {
    // 可选：展示空图表或 return 一个空 option
    return {
      title: {
        text: i18n.global.t('common.data.empty'),
        left: 'center',
        top: 'middle',
        textStyle: {
          color: '#ccc',
          fontSize: 16
        }
      }
    }
  }
  const [sendUnreplied, sendReplied, recvUnreplied, recvReplied] = seriesData.value;

  const baseLabel = {
    show: true,
    color: '#fff',
    fontWeight: 'bold',
    position: 'inside'
  };

  const yAxisConfig = getYAxisConfig(dateType.value);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {type: 'shadow'}
    },
    legend: {
      data: [
        i18n.global.t('home.email.e.chart.replay.1'),
        i18n.global.t('home.email.e.chart.replay.2'),
        i18n.global.t('home.email.e.chart.replay.3'),
        i18n.global.t('home.email.e.chart.replay.4')],
      left: 0,   // 靠左
      bottom: 20      // 底部对齐
    },
    grid: {
      left: 0,     // 控制图表左边距
      right: 0,    // 控制图表右边距
      top: 40,      // 可以根据需要调整
      bottom: 80,   // 留给 legend 使用
      containLabel: true // 避免坐标轴标签被裁剪
    },
    xAxis: {
      data: xData.value,
      name: i18n.global.t('common.time'),
      axisLine: {onZero: true},
      splitLine: {show: false}
    },
    yAxis: {
      type: 'value',
      ...yAxisConfig,
    },
    series: [
      {
        ...sendUnreplied,
        name: i18n.global.t('home.email.e.chart.replay.1'),
        stack: 'send',
        itemStyle: {color: '#7d9ff3'},
        label: {
          ...baseLabel,
          formatter: '{c}',
          color: '#333333',
          textBorderColor: '#fff', // 白色描边
          textBorderWidth: 2,       // 描边宽度
        }
      },
      {
        ...sendReplied,
        name: i18n.global.t('home.email.e.chart.replay.2'),
        stack: 'send',
        itemStyle: {color: '#9d5ff1'},
        label: {
          ...baseLabel,
          formatter: '{c}',
          color: '#333333',
          textBorderColor: '#fff', // 白色描边
          textBorderWidth: 2,       // 描边宽度
        }
      },
      {
        name: i18n.global.t('home.email.response.rate'),
        type: 'bar',
        stack: 'send',
        data: xData.value.map((_, i) => {
          const unreplied = seriesData.value[0].data[i] || 0;
          const replied = seriesData.value[1].data[i] || 0;
          const total = unreplied + replied;
          return total === 0 ? 0 : (replied / total).toFixed(2);
        }),
        itemStyle: {
          color: 'transparent', // 一定是 transparent
          borderWidth: 0,
          shadowBlur: 0
        },
        label: {
          show: true,
          position: 'top',
          color: '#333333',
          textBorderColor: '#fff', // 白色描边
          textBorderWidth: 2,       // 描边宽度
          fontWeight: 'bold',
          formatter: (params) => {
            const index = params.dataIndex;
            const unreplied = seriesData.value[0].data[index] || 0;
            const replied = seriesData.value[1].data[index] || 0;
            const total = unreplied + replied;
            if (total === 0) return '';
            return `${Math.round((replied / total) * 100)}%`;
          }
        }
      },
      {
        ...recvUnreplied,
        name: i18n.global.t('home.email.e.chart.replay.3'),
        stack: 'recv',
        itemStyle: {color: '#8edbb6'},
        label: {
          ...baseLabel,
          formatter: '{c}',
          color: '#333333',
          textBorderColor: '#fff', // 白色描边
          textBorderWidth: 2,       // 描边宽度
        }
      },
      {
        ...recvReplied,
        name: i18n.global.t('home.email.e.chart.replay.4'),
        stack: 'recv',
        itemStyle: {color: '#efb47d'},
        label: {
          ...baseLabel,
          formatter: '{c}',
          color: '#333333',
          textBorderColor: '#fff', // 白色描边
          textBorderWidth: 2,       // 描边宽度
        }
      },
      {
        name: i18n.global.t('home.email.receive.rate'),
        type: 'bar',
        stack: 'recv',
        data: xData.value.map((_, i) => {
          const unreplied = seriesData.value[2].data[i] || 0;
          const replied = seriesData.value[3].data[i] || 0;
          const total = unreplied + replied;
          return total === 0 ? 0 : (replied / total).toFixed(2);
        }),
        itemStyle: {
          color: 'transparent', // 一定是 transparent
          borderWidth: 0,
          shadowBlur: 0
        },
        label: {
          show: true,
          position: 'top',
          color: '#333333',
          textBorderColor: '#fff', // 白色描边
          textBorderWidth: 2,       // 描边宽度
          fontWeight: 'bold',
          formatter: (params) => {
            const index = params.dataIndex;
            const unreplied = seriesData.value[2].data[index] || 0;
            const replied = seriesData.value[3].data[index] || 0;
            const total = unreplied + replied;
            if (total === 0) return '';
            return `${Math.round((replied / total) * 100)}%`;
          }
        }
      }
    ]
  };
});

const barWidthPerX = lang.value !== 'zh-CN' ? 130 : 100;
const chartWidth = computed(() => Math.max(678, xData.value.length * barWidthPerX));

function updateChart() {
  if (!chartRef.value) return;

  nextTick(() => {
    if (!chartInstance) {
      chartInstance = initEcharts(chartRef.value);
    }

    const option = chartOption.value;

    chartInstance.clear(); // 清除旧图表

    chartInstance.setOption(option, true);
  })
}

function resizeChart() {
  chartInstance?.resize();
}


const resetEcharts = async () => {
  isDataReady.value = false
  if (selectedMember.value) {
    await store.dispatch('homeEcharts/getEmailEchartsApis', {
      type: 2,
      dayType: dateType.value,
      teamId: props.element.teamId,
      userId: selectedMember.value === i18n.global.t('common.all') ? '' : selectedMember.value
    })
  }
  isDataReady.value = true
}

watch(() => selectedMember.value, async (userId) => {
  if (userId) {
    await resetEcharts()
  }
}, {immediate: true})

watch(() => dateType.value, async (newValue) => {
  if (newValue) {
    await resetEcharts()
  }
}, {immediate: true})

watch(() => isDataReady.value, async (ready) => {
  if (ready) {
    await updateChart()
  }
})

watch(() => chartWidth.value, async (newValue) => {
  if (newValue) {
    await nextTick() // 等待 DOM 变化完成
    chartInstance.resize()
  }
})

onMounted(() => {
  window.addEventListener('resize', resizeChart);
});

onBeforeUnmount(() => {
  chartInstance?.dispose();
  window.removeEventListener('resize', resizeChart);
});
</script>

<style scoped lang="less">
.toolbar {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.chart-scroll-wrapper {
  overflow-x: auto;
  width: 100%;
}

.chart-inner {
  min-width: 600px;
}

:deep(.el-radio__inner) {
  display: none !important;
}

</style>